import { ExtendedTabProps } from "@components/common/tabs/tabs-type";
import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { SdbCompany } from "@custom-types/sdb-company-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { MembersPage } from "@pages/members/members-page";
import { Teams } from "@pages/members/teams/teams";
import { NotFoundPage } from "@pages/not-found-page";
import { UsersTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { TEAMS_DISPLAY_NAME } from "@src/constants/team-constants";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { RequiredRoleCompanyLevelName } from "@utils/access-control/company/company-access-control-types";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { OpenTabEvents } from "@utils/track-event/track-event-list";

/** Content that will have the logic for the users tabs */
export function MembersWorkspace(): JSX.Element {
  const { usersTab } = useAppParams();
  const { companyId } = useAppParams();
  const { canViewTeams } = useHasUserValidRoleCompanyLevel();
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);

  if (!companyId || !usersTab || !Object.values(UsersTabs).includes(usersTab)) {
    return <NotFoundPage />;
  }

  const usersTabs: ExtendedTabProps<UsersTabs, SdbCompany | null>[] = [
    {
      label: "Members",
      route: UsersTabs.members,
      content: () => <MembersPage />,
      loadingContent: <div />,
    },
    {
      label: capitalizeFirstLetter(TEAMS_DISPLAY_NAME),
      route: UsersTabs.teams,
      content: () => <Teams companyId={companyId} />,
      loadingContent: <div />,
      requiredRoleCompanyLevel: RequiredRoleCompanyLevelName.canViewTeams,
      isHidden: !canViewTeams,
    },
  ];

  return (
    <TabsWithRoutes
      dataTestId="USER_CONTAINER"
      selectedTab={usersTab}
      requiredAttribute={selectedSdbCompany}
      trackingEventPageName={OpenTabEvents.openWorkspaceUsersTab}
      tabs={usersTabs}
    />
  );
}
